import { Injectable } from '@angular/core';

import { select, Store } from '@ngrx/store';

import  { ShipmentsActions } from './shipments.actions';
import * as ShipmentsSelectors from './shipments.selectors';
import {
    Shipment,
    SearchQuery,
    BackendQuery,
    ShipmentData,
    ShipmentTrackingRequest,
    ShipmentState,
} from './shipments.models';
import { loadShipment } from '@qwyk/shared-stores/shipment-shared-actions';

@Injectable()
export class ShipmentsFacade {
    /**
     * Combine pieces of state using createSelector,
     * and expose them as observables through the facade.
     */
    loading$ = this.store.pipe(select(ShipmentsSelectors.getShipmentsLoading));
    error$ = this.store.pipe(select(ShipmentsSelectors.getShipmentsError));
    allShipments$ = this.store.pipe(select(ShipmentsSelectors.getAllShipments));
    selectedShipments$ = this.store.pipe(select(ShipmentsSelectors.getSelected));
    customFields$ = this.store.pipe(select(ShipmentsSelectors.getCustomFields));
    query$ = this.store.pipe(select(ShipmentsSelectors.getShipmentsQuery));
    backendQuery$ = this.store.pipe(select(ShipmentsSelectors.getBackendQuery));
    sysStates$ = this.store.pipe(select(ShipmentsSelectors.getSysStates));
    loadingSysStates$ = this.store.pipe(select(ShipmentsSelectors.getSysStatesLoading));
    selectedSysState$ = this.store.pipe(select(ShipmentsSelectors.getCurrentSysState));
    filteredShipments$ = this.store.select(ShipmentsSelectors.selectFilteredShipments);
    pagination$ = this.store.pipe(select(ShipmentsSelectors.getBackendPagination));

    constructor(private store: Store) {}

    /**
     * Use the initialization action to perform one
     * or more tasks in your Effects.
     */
    public loadShipments(query: BackendQuery) {
        this.store.dispatch(ShipmentsActions.loadShipments({ query }));
    }

    public loadDraftShipmentsWithDetails(query: BackendQuery, includeStates: ShipmentState[]) {
        this.store.dispatch(ShipmentsActions.loadDraftShipments({ query, includeStates }));
    }

    public loadSysStates() {
        this.store.dispatch(ShipmentsActions.loadSysStates());
    }

    public resetLoadShipmentsQuery() {
        this.store.dispatch(ShipmentsActions.resetLoadShipmentsQuery());
    }

    public searchShipments(query: SearchQuery) {
        this.store.dispatch(ShipmentsActions.searchShipments({ query }));
    }

    public loadShipment(shipmentId: string): void {
        this.store.dispatch(loadShipment({ shipmentId }));
    }

    public selectShipment(selectedId: string): void {
        this.store.dispatch(ShipmentsActions.selectShipment({ selectedId }));
    }

    public unselectShipment(): void {
        this.store.dispatch(ShipmentsActions.unselectShipment());
    }

    public updateShipmentReferences(
        shipmentId: string,
        update: Partial<Shipment>
    ): void {
        this.store.dispatch(
            ShipmentsActions.updateShipmentReferences({ shipmentId, update })
        );
    }

    public generatePdf(shipmentId: string): void {
        this.store.dispatch(
            ShipmentsActions.generateShipmentPdf({ shipmentId })
        );
    }

    public sendTrackingRequest(event: {
        shipmentId: string;
        trackingRequest: ShipmentTrackingRequest;
    }): void {
        this.store.dispatch(ShipmentsActions.sendTrackingRequest(event));
    }

    public cancelTrackingRequest(event) {
        this.store.dispatch(ShipmentsActions.cancelTrackingRequest(event));
    }

    public updateShipmentDataTransport(
        shipmentId: string,
        changes: Partial<ShipmentData>
    ): void {
        this.store.dispatch(
            ShipmentsActions.updateShipmentDataTransport({
                shipmentId,
                changes,
            })
        );
    }

    public updateShipmentDataReferences(
        shipmentId: string,
        changes: Partial<ShipmentData>
    ): void {
        this.store.dispatch(
            ShipmentsActions.updateShipmentDataReferences({
                shipmentId,
                changes,
            })
        );
    }

    public assignShipmentOperator(
        shipmentId: string,
        operatorId: number
    ): void {
        this.store.dispatch(
            ShipmentsActions.assignShipmentOperator({ shipmentId, operatorId })
        );
    }

    public completeShipment(shipmentId: string): void {
        this.store.dispatch(ShipmentsActions.completeShipment({ shipmentId }));
    }

    public cancelShipment(shipmentId: string): void {
        this.store.dispatch(ShipmentsActions.cancelShipment({ shipmentId }));
    }

    public dismissError(): void {
        this.store.dispatch(ShipmentsActions.dismissError());
    }

    public updateShipmentName(shipmentId: string, update: { name: string }) {
        this.store.dispatch(
            ShipmentsActions.updateShipmentName({ shipmentId, update })
        );
    }

    public updateShipmentPartially(
        shipmentId: string,
        update: Partial<Shipment>
    ): void {
        this.store.dispatch(
            ShipmentsActions.updateShipmentPartiallySuccess({ shipmentId, update })
        );
    }

    public reset() {
        this.store.dispatch(ShipmentsActions.resetAction());
    }
}
